// ============================================
// STANDARDIZED AUTH QUERY HOOK
// Base hook for all auth-related queries with consistent patterns
// ============================================

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { handleQueryError, createRetryStrategy, createRetryDelay } from '@/lib/errorHandler';
import type { UserRoleData, AdminCheckResult, Role, Permission, RolePermission, UserProfile, UserWithRoles } from '@/types/auth';

// ==================== QUERY CONFIGURATION ====================

export const AUTH_QUERY_CONFIG = {
  staleTime: 20 * 60 * 1000, // 20 minutes - longer cache to reduce requests
  gcTime: 60 * 60 * 1000,    // 60 minutes - longer garbage collection
  retry: createRetryStrategy(1), // Reduced retries for auth
  retryDelay: createRetryDelay(1500), // Faster retry for auth
  refetchOnWindowFocus: false, // Prevent excessive refetching
  refetchOnReconnect: false,   // Prevent network reconnection spam
  refetchOnMount: false,       // Only fetch when actually needed
  timeout: 10000,              // 10s timeout for auth queries
} as const;

// ==================== BASE AUTH QUERY HOOK ====================

interface UseAuthQueryOptions<T> extends Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'> {
  queryKey: (string | undefined)[];
  queryFn: () => Promise<T>;
  requiresAuth?: boolean;
  requiresSession?: boolean;
}

export function useAuthQuery<T = unknown>({
  queryKey,
  queryFn,
  requiresAuth = true,
  requiresSession = false,
  enabled = true,
  retry = 2,
  retryDelay = 1000,
  ...options
}: UseAuthQueryOptions<T>) {
  const { user, session, loading, isDemoMode, isPreviewMode } = useAuth();

  // Only use mock data in demo mode, not preview mode
  const getDemoMockData = (queryKeyStr: string): T => {
    const demoId = crypto.randomUUID();
    
    if (queryKeyStr.includes('user-roles')) {
      return [{ 
        role_code: 'ROLE_BASIC', 
        role_name: 'Demo User' 
      }] as T;
    }
    if (queryKeyStr.includes('admin-check') || queryKeyStr.includes('admin-fallback-check')) {
      return false as T;
    }
    if (queryKeyStr.includes('check-permission')) {
      return true as T; // Allow all permissions in demo
    }
    if (queryKeyStr.includes('roles')) {
      return [{ 
        id: demoId, 
        role_code: 'ROLE_BASIC', 
        role_name: 'Demo Role', 
        is_active: true,
        description: 'Demo role for preview',
        sort_order: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }] as T;
    }
    if (queryKeyStr.includes('permissions')) {
      return [{ 
        id: demoId, 
        permission_code: 'VIEW_PROJECTS', 
        permission_name: 'View Projects',
        description: 'Demo permission',
        resource: 'projects',
        action: 'view',
        created_at: new Date().toISOString()
      }] as T;
    }
    if (queryKeyStr.includes('role-permissions')) {
      return [{ 
        role_id: demoId,
        permission_id: demoId
      }] as T;
    }
    if (queryKeyStr.includes('user-profiles')) {
      return [{ 
        id: user?.id || demoId, 
        email: 'demo@preview.com', 
        full_name: 'Demo User',
        avatar_url: null,
        company: 'ENP',
        default_role_id: demoId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }] as T;
    }
    if (queryKeyStr.includes('users-with-roles')) {
      return [{ 
        id: user?.id || demoId, 
        email: 'demo@preview.com', 
        full_name: 'Demo User', 
        avatar_url: null,
        company: 'ENP',
        roles: [{ role_code: 'ROLE_BASIC', role_name: 'Demo Role' }]
      }] as T;
    }
    
    // Handle other common query patterns that might be causing issues
    if (queryKeyStr.includes('projects')) {
      return [] as T; // Empty projects array for demo
    }
    if (queryKeyStr.includes('items')) {
      return [] as T; // Empty items array for demo  
    }
    
    // For completely unknown queries, return appropriate empty structures
    return [] as T;
  };

  // Enhanced enabled condition with better preview mode support
  const sessionReady = !!session?.access_token;
  
  const isEnabled = 
    enabled &&
    !loading &&
    (!requiresAuth || !!user?.id) &&
    (!requiresSession || sessionReady);

  // Return mock data only in demo mode, not preview mode (skip database entirely)
  if (isDemoMode && !isPreviewMode) {
    const mockData = getDemoMockData(queryKey.join('-'));
    return {
      data: mockData,
      isLoading: false,
      isError: false,
      error: null,
      isSuccess: true,
      isFetching: false,
      isStale: false,
      refetch: () => Promise.resolve({ data: mockData }),
    } as any;
  }

  // ✅ REMOVED: Early return for preview mode - let React Query handle caching naturally
  // Previously this bypassed React Query lifecycle causing reload on back navigation

  // Wrap queryFn with error handling
  const wrappedQueryFn = async () => {
    // Phase 2 Week 6.2: Prevent anon fetch when session required
    if (requiresSession && !session?.access_token) {
      throw new Error('auth-session-not-ready');
    }

    try {
      const result = await queryFn();
      
      // Debug logging for permission checks (only when flag enabled)
      if (import.meta.env.VITE_DEBUG_PERMISSIONS === 'true' && queryKey[0] === 'check-permission') {
        console.log('[DEBUG_PERMISSIONS]', {
          permissionCode: queryKey[1],
          userId: user?.id?.slice(0, 8) + '...',
          email: user?.email,
          result,
          supabaseUrl: 'https://dyakqbhcepjcommcflru.supabase.co',
          timestamp: new Date().toISOString()
        });
      }
      
      return result;
    } catch (error) {
      handleQueryError(error, `useAuthQuery:${queryKey.join('-')}`);
      throw error; // Re-throw to maintain normal React Query error handling
    }
  };

  return useQuery({
    queryKey: [...queryKey, user?.id ?? 'anon'], // ✅ Stable key: removed access_token to prevent cache misses
    queryFn: wrappedQueryFn,
    enabled: isEnabled,
    ...AUTH_QUERY_CONFIG,
    ...options,
  });
}

// ==================== SPECIALIZED AUTH HOOKS ====================

export function useUserRolesQuery() {
  const { user } = useAuth();
  return useAuthQuery<UserRoleData[]>({
    queryKey: ['user-roles', user?.id],
    queryFn: async () => {
      const { fetchUserRoles } = await import('@/lib/queries/authQueries');
      return fetchUserRoles(user?.id || '');
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id,
    initialData: [],
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useAdminCheckQuery() {
  const { user } = useAuth();
  return useAuthQuery<boolean>({
    queryKey: ['admin-check', user?.id],
    queryFn: async () => {
      const { checkUserIsAdmin } = await import('@/lib/queries/authQueries');
      return checkUserIsAdmin(user?.id || '');
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id,
    initialData: false,
    staleTime: 5 * 60 * 1000, // Cache admin status for 5 minutes
  });
}

export function usePermissionCheckQuery(permissionCode: string) {
  const { user } = useAuth();
  return useAuthQuery<boolean>({
    queryKey: ['check-permission', permissionCode, user?.id],
    queryFn: async () => {
      const { checkUserPermission } = await import('@/lib/queries/authQueries');
      return checkUserPermission(user?.id || '', permissionCode);
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!permissionCode && !!user?.id,
  });
}

// ==================== ADMIN DATA HOOKS ====================

export function useRolesQuery(activeOnly: boolean = false) {
  return useAuthQuery<Role[]>({
    queryKey: ['roles', activeOnly ? 'active' : 'all'],
    queryFn: async () => {
      const { fetchRoles } = await import('@/lib/queries/authQueries');
      return fetchRoles(activeOnly);
    },
    requiresAuth: true,
  });
}

export function usePermissionsQuery() {
  return useAuthQuery<Permission[]>({
    queryKey: ['permissions'],
    queryFn: async () => {
      const { fetchPermissions } = await import('@/lib/queries/authQueries');
      return fetchPermissions();
    },
    requiresAuth: true,
  });
}

export function useRolePermissionsQuery(roleId?: string) {
  return useAuthQuery<RolePermission[]>({
    queryKey: ['role-permissions', roleId || 'matrix'],
    queryFn: async () => {
      const { fetchRolePermissions } = await import('@/lib/queries/authQueries');
      return fetchRolePermissions(roleId);
    },
    requiresAuth: true,
    enabled: !!roleId || roleId === undefined, // Allow undefined for matrix view
  });
}

export function useUserProfilesQuery() {
  return useAuthQuery<UserProfile[]>({
    queryKey: ['user-profiles'],
    queryFn: async () => {
      const { fetchUserProfiles } = await import('@/lib/queries/authQueries');
      return fetchUserProfiles();
    },
    requiresAuth: true,
  });
}

export const useUsersWithRolesQuery = () => {
  return useAuthQuery<UserWithRoles[]>({
    queryKey: ['users-with-roles'],
    queryFn: async () => {
      const { fetchUsersWithRoles } = await import('@/lib/queries/authQueries');
      return fetchUsersWithRoles();
    },
    requiresAuth: true,
    requiresSession: true,
  });
};

// Hook to fetch ALL user-role mappings for admin matrix views
export interface AllUserRoleMapping {
  user_id: string;
  role_id: string;
  is_active: boolean;
}

export function useAllUserRolesQuery() {
  return useAuthQuery<AllUserRoleMapping[]>({
    queryKey: ['all-user-roles-mapping'],
    queryFn: async () => {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .from('user_roles')
        .select('user_id, role_id, is_active')
        .eq('is_active', true);
      
      if (error) throw error;
      return data || [];
    },
    requiresAuth: true,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}